import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Mail, ArrowRight } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { whatsappUrl, mailtoUrl, HERO_IMAGE } from "@/lib/brand";

const fade = {
  hidden: { opacity: 0, y: 24, filter: "blur(8px)" },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { t } = useTranslation();
  const talkMessage = t("common.defaultWhatsappMessage");
  const emailSubject = t("common.defaultEmailSubject");

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative pt-28 md:pt-24 pb-16 md:pb-24 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        <div className="relative z-10">
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={0}
            className="font-body uppercase tracking-[0.28em] text-xs md:text-sm text-[#C86A3D] font-semibold mb-6"
          >
            {t("hero.eyebrow")}
          </motion.p>

          <motion.h1
            variants={fade}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-display font-black tracking-tighter text-[#1A1A1A] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]"
          >
            <Trans
              i18nKey="hero.title"
              components={{
                em: (
                  <span className="font-serif-editorial italic font-normal text-[#2A3563]" />
                ),
              }}
            />
          </motion.h1>

          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={2}
            className="font-body text-base md:text-lg text-[#1A1A1A]/70 mt-7 max-w-lg"
          >
            {t("hero.description")}
          </motion.p>

          <motion.div
            variants={fade}
            initial="hidden"
            animate="show"
            custom={3}
            className="flex flex-col sm:flex-row gap-4 mt-9"
          >
            <a
              href={whatsappUrl(talkMessage)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-body font-semibold px-7 py-4 rounded-sm hover:brightness-95 transition-all"
            >
              <MessageCircle size={20} />
              {t("hero.whatsappCta")}
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
            <a
              href={mailtoUrl(emailSubject)}
              data-testid="hero-email-btn"
              className="inline-flex items-center justify-center gap-2 border border-[#2A3563] text-[#2A3563] font-body font-semibold px-7 py-4 rounded-sm hover:bg-[#2A3563] hover:text-white transition-colors"
            >
              <Mail size={18} /> {t("hero.emailCta")}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute -top-6 -right-6 w-2/3 h-full bg-[#2A3563] rounded-sm hidden lg:block" />
          <div className="relative overflow-hidden rounded-sm border border-[#E5E5E0] aspect-[4/5] shadow-xl">
            <img
              src={HERO_IMAGE}
              alt={t("hero.imageAlt")}
              fetchPriority="high"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-6">
              <span className="font-display text-white/90 uppercase tracking-widest text-xs">
                {t("hero.collectionLabel")}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
